import { Stack, Typography } from '@mui/material'
import { useMinimalsTheme } from '@tom-ui/ui'
import { MAX_INT32_VALUE } from '@tom-ui/utils'
import { ConfigAreaDto, ConfigAreaType } from '@yard/app/api'
import { formatRowName, isConfigAreaBlocked } from '@yard/utils/yard-block.util'
import { memo, useState } from 'react'
import { BAY_WIDTH, ROW_HEIGHT } from '../../helpers/yard-view.utils'
import { MaxStackHeight } from './MaxStackHeight'
import { YardAxisContextMenu } from './YardAxisContextMenu'

interface Props {
  maxRows: number
  rowNames: string[]
  isEditable: boolean
  configAreas: ConfigAreaDto[]
  blockName?: string
  onClick: (row: number) => void
  onRenameRow: (row: number) => void
  onBlockConfigArea: (minBay: number, maxBay: number, minRow: number, maxRow: number) => void
  onDeleteConfigArea: (
    configAreaType: ConfigAreaType,
    minBay: number,
    maxBay: number,
    minRow: number,
    maxRow: number,
  ) => void
  onAdjustMaxTier: (row: number) => void
  onInsertRowUp: (row: number) => void
  onInsertRowDown: (row: number) => void
  onDeleteRow: (row: number) => void
}
export const YardBlockRowHeader = memo(function YardBlockRowHeader(props: Props) {
  const {
    maxRows,
    rowNames,
    isEditable,
    configAreas,
    blockName,
    onClick,
    onRenameRow,
    onBlockConfigArea,
    onDeleteConfigArea,
    onAdjustMaxTier,
    onInsertRowUp,
    onInsertRowDown,
    onDeleteRow,
  } = props
  const theme = useMinimalsTheme()

  const [contextMenu, setContextMenu] = useState<{
    mouseX: number
    mouseY: number
    rowIndex: number
  } | null>(null)

  const MENU_WIDTH = 250

  const [highlightedRow, setHighlightedRow] = useState<number | null>(null)
  const rowNumber = highlightedRow !== null ? highlightedRow + 1 : null

  const handleRowContextMenu = (e: React.MouseEvent, rowIndex: number) => {
    if (!isEditable) return

    e.preventDefault()
    e.stopPropagation()

    setHighlightedRow(rowIndex)

    const currentElementBoundingRect = e.currentTarget.getBoundingClientRect()
    const rowCenterY = currentElementBoundingRect.top + ROW_HEIGHT / 2

    setContextMenu({
      mouseX: currentElementBoundingRect.right,
      mouseY: rowCenterY - MENU_WIDTH / 2,
      rowIndex,
    })
  }

  return (
    <Stack>
      <Stack
        sx={{
          height: ROW_HEIGHT,
          width: BAY_WIDTH,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          paddingLeft: theme.customSpacing.s,
          paddingRight: theme.customSpacing.s,
          backgroundColor: blockName ? theme.palette.grey[200] : 'transparent',
          border: blockName ? `1px solid ${theme.palette.grey[300]}` : 'none',
          borderTopLeftRadius: blockName ? theme.shape.borderRadius : 0,
          overflow: 'hidden',
        }}
      >
        {blockName && (
          <Typography
            variant='subtitle2'
            overflow='hidden'
            textOverflow='ellipsis'
            whiteSpace='nowrap'
            width='100%'
            title={blockName}
          >
            {blockName}
          </Typography>
        )}
      </Stack>
      <YardAxisContextMenu
        axis='row'
        open={contextMenu !== null}
        width={MENU_WIDTH}
        anchorPosition={
          contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
        }
        onClose={() => {
          setContextMenu(null)
          setTimeout(() => {
            setHighlightedRow(null)
          }, 100)
        }}
        onRename={() => {
          if (rowNumber === null) return
          onRenameRow(rowNumber)
        }}
        onInsertBefore={() => {
          if (rowNumber === null) return
          onInsertRowUp(rowNumber)
        }}
        onInsertAfter={() => {
          if (rowNumber === null) return
          onInsertRowDown(rowNumber)
        }}
        onDelete={() => {
          if (rowNumber === null) return
          onDeleteRow(rowNumber)
        }}
        onAdjustMaxTier={() => {
          if (rowNumber === null) return
          onAdjustMaxTier(rowNumber)
        }}
        onBlockConfigArea={
          rowNumber !== null &&
          !isConfigAreaBlocked(configAreas, 1, MAX_INT32_VALUE, rowNumber, rowNumber)
            ? () => onBlockConfigArea(1, MAX_INT32_VALUE, rowNumber, rowNumber)
            : undefined
        }
        onDeleteConfigArea={
          rowNumber !== null &&
          isConfigAreaBlocked(configAreas, 1, MAX_INT32_VALUE, rowNumber, rowNumber)
            ? configAreaType =>
                onDeleteConfigArea(configAreaType, 1, MAX_INT32_VALUE, rowNumber, rowNumber)
            : undefined
        }
      />

      {Array.from({ length: maxRows }).map((_, rowIndex) => {
        const rowNumber = rowIndex + 1
        return (
          <Stack
            key={rowIndex}
            sx={{
              height: ROW_HEIGHT,
              minWidth: BAY_WIDTH,
              display: 'flex',
              flexDirection: 'row',
              gap: theme.customSpacing.xxxs,
              alignItems: 'center',
              justifyContent: 'center',
              paddingLeft: theme.customSpacing.s,
              paddingRight: theme.customSpacing.s,
              backgroundColor: theme.palette.grey[200],
              border: `1px solid ${theme.palette.grey[300]}`,
              borderTopLeftRadius: rowIndex === 0 && !blockName ? theme.shape.borderRadius : 0,
              borderBottomLeftRadius: rowIndex === maxRows - 1 ? theme.shape.borderRadius : 0,
              borderColor:
                highlightedRow === rowIndex ? theme.palette.primary.main : theme.palette.grey[300],
            }}
            onContextMenu={e => handleRowContextMenu(e, rowIndex)}
            onDoubleClick={e => {
              e.stopPropagation()
              onClick(rowNumber)
            }}
          >
            <Typography
              variant='subtitle2'
              overflow='hidden'
              textOverflow='ellipsis'
              width='100%'
              whiteSpace='nowrap'
              title={formatRowName(rowNames, rowIndex)}
            >
              {formatRowName(rowNames, rowIndex)}
            </Typography>

            <MaxStackHeight
              configAreas={configAreas}
              minBay={1}
              maxBay={MAX_INT32_VALUE}
              minRow={rowNumber}
              maxRow={rowNumber}
              sx={{
                color: theme.palette.grey[500],
              }}
            />
          </Stack>
        )
      })}
    </Stack>
  )
})
